import { useFormContext } from "react-hook-form";

import RequiredFooter from "@/components/RequiredFooter";
import { ULThemeCheckbox } from "@/components/ULThemeCheckbox";
import { ULThemeButton } from "@/components/ULThemeButton";
import { ULThemeFormMessage } from "@/components/form/ULThemeFormMessage";
import { FormField, FormItem } from "@/components/ui/form";

import locales from "../../locales/en.json";
import type { WizardFormValues } from "./types";

interface CheckDetailsStepProps {
  onEdit: () => void;
  onBack: () => void;
  onJoin: () => void;
  isSubmitting: boolean;
  email?: string;
  // Bot-detection widget. This is Flow A's terminal sub-step, so the challenge
  // is issued here rather than earlier in the wizard — Turnstile tokens expire.
  // Owned by SignupPasswordForm; see the comment there.
  captchaSlot?: React.ReactNode;
  blockPrimary?: boolean;
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-1.5 text-sm border-b" style={{ borderColor: "#F3F4F6" }}>
      <span style={{ color: "#6B7280" }}>{label}</span>
      <span style={{ color: "#1D2B4F" }} className="font-medium text-right">
        {value || "—"}
      </span>
    </div>
  );
}

function CheckDetailsStep({
  onEdit,
  onBack,
  onJoin,
  isSubmitting,
  email,
  captchaSlot = null,
  blockPrimary = false,
}: CheckDetailsStepProps) {
  const form = useFormContext<WizardFormValues>();
  const values = form.watch();

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-semibold" style={{ color: "#1D2B4F" }}>
          Your details
        </h3>
        <button
          type="button"
          onClick={onEdit}
          className="text-sm underline"
          style={{ color: "#4A44F2" }}
        >
          {locales.form.editLink}
        </button>
      </div>

      <div className="mb-4 rounded-lg p-3" style={{ backgroundColor: "#F9FAFB" }}>
        {email && <ReviewRow label="Email" value={email} />}
        <ReviewRow label={locales.form.fields.title.label} value={values.title} />
        <ReviewRow label={locales.form.fields.gender.label} value={values.gender} />
        <ReviewRow label={locales.form.fields.firstName.label} value={values.firstName} />
        <ReviewRow label={locales.form.fields.lastName.label} value={values.lastName} />
        <ReviewRow label={locales.form.fields.dateOfBirth.label} value={values.dateOfBirth} />
        <ReviewRow label={locales.form.fields.mobile.label} value={values.mobile} />
        <ReviewRow label={locales.form.fields.country.label} value={values.country} />
        <ReviewRow label={locales.form.fields.residentialAddress.label} value={values.residentialAddress} />
        <ReviewRow
          label={locales.form.fields.postalAddress.label}
          value={values.postalSameAsResidential ? "Same as residential" : values.postalAddress}
        />
        <ReviewRow
          label="Tax file number"
          value={values.tfn ? "•••••••••" : "Not provided"}
        />
      </div>

      <FormField
        control={form.control}
        name="consentAccepted"
        rules={{ required: locales.form.fields.consent.required }}
        render={({ field, fieldState }) => (
          <FormItem>
            <div
              className="flex items-start gap-2 mb-1 p-3 rounded-lg"
              style={{ backgroundColor: "#E6F7F1" }}
            >
              <ULThemeCheckbox
                checked={field.value}
                onCheckedChange={field.onChange}
                id="declaration-consent"
              />
              <label htmlFor="declaration-consent" className="text-sm theme-universal:text-body-text">
                {locales.form.fields.consent.label}
              </label>
            </div>
            <ULThemeFormMessage hasFormError={!!fieldState.error} />
          </FormItem>
        )}
      />

      <p className="text-xs mb-4" style={{ color: "#6B7280" }}>
        {locales.form.declarationFooter}
      </p>

      {captchaSlot}

      <div className="flex gap-3">
        <ULThemeButton type="button" variant="outline" className="flex-1" onClick={onBack}>
          {locales.form.backButton}
        </ULThemeButton>
        <ULThemeButton
          type="button"
          className="flex-1"
          onClick={onJoin}
          disabled={isSubmitting || blockPrimary}
        >
          {locales.form.joinButton}
        </ULThemeButton>
      </div>
      <RequiredFooter />
    </div>
  );
}

export default CheckDetailsStep;
